/* @jsx h */
import { defineComponent, h } from "vue";
import { Loading } from "@app/vue/components/index";
import { formatNumber, formatPriceInLocale } from "@lemmonhq/orders";
import { useLocalization } from "@lemmonhq/uikit";
import { useAuth } from "@app/vue/providers/Auth";
import { WeeklyPerformanceDatum } from "@lemmonhq/types";
import { WeeklyHeatmapFiltersProps } from "./WeeklyHeatmapFilters";

const DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const DAY_TRANSLATION_KEYS: { [key: string]: string } = {
  Monday: "reports.heatmap.monday",
  Tuesday: "reports.heatmap.tuesday",
  Wednesday: "reports.heatmap.wednesday",
  Thursday: "reports.heatmap.thursday",
  Friday: "reports.heatmap.friday",
  Saturday: "reports.heatmap.saturday",
  Sunday: "reports.heatmap.sunday",
};

const START_HOUR = 6;
const END_HOUR = 26;
const HOURS = Array.from(
  { length: END_HOUR - START_HOUR + 1 },
  (_, i) => START_HOUR + i,
);

function formatHour(hour: number): string {
  const adjustedHour = hour % 24;
  const period = adjustedHour >= 12 ? "PM" : "AM";
  const displayHour =
    adjustedHour === 0
      ? 12
      : adjustedHour > 12
        ? adjustedHour - 12
        : adjustedHour;
  return `${displayHour}${period}`;
}

const colors = [
  "#b3d6ff",
  "#80bbff",
  "#4da0ff",
  "#1a85ff",
  "#006ce6",
  "#0054b3",
  "#003c80",
];

function getMedian(values: number[]): number {
  const sorted = values.filter((v) => v > 0).sort((a, b) => a - b);
  if (sorted.length === 0) return 0;
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? (sorted[mid - 1] + sorted[mid]) / 2
    : sorted[mid];
}

function getHeatmapColor(
  value: number,
  median: number,
  maxValue: number,
): string {
  if (value === 0) return "transparent";

  // Use median as the midpoint for color scaling
  // Values below median: lighter colors (0-3)
  // Values above median: darker colors (4-6)
  let normalizedValue: number;

  if (value <= median) {
    // Map [0, median] to [0, 0.5]
    normalizedValue = (value / median) * 0.5;
  } else {
    // Map [median, max] to [0.5, 1]
    normalizedValue = 0.5 + ((value - median) / (maxValue - median)) * 0.5;
  }

  const index = Math.min(
    Math.floor(normalizedValue * colors.length),
    colors.length - 1,
  );
  return colors[index];
}

export const WeeklyHeatmapChart = defineComponent({
  name: "WeeklyHeatmapChart",
  props: {
    data: { type: Array as () => WeeklyPerformanceDatum[], required: true },
    loading: { type: Boolean, default: false },
    filters: {
      type: Object as () => WeeklyHeatmapFiltersProps["filters"],
      required: true,
    },
  },
  setup(props) {
    const auth = useAuth();
    const { t } = useLocalization();
    return () => {
      if (props.loading) {
        return (
          <div
            key="loading"
            class="w-full h-[400px] flex justify-center items-center"
          >
            <Loading />
          </div>
        );
      }

      const processedData = DAYS.map((day) =>
        HOURS.map((hour) => {
          const adjustedHour = hour % 24;
          const matchingData = props.data.filter(
            (d) => d.day_of_week === day && d.hour_of_day === adjustedHour,
          );
          return matchingData.reduce((sum, curr) => sum + curr.value, 0);
        }),
      );

      const allValues = processedData.flat();
      const minValue = Math.min(...allValues.filter((el) => el > 0));
      const maxValue = Math.max(...allValues);
      const medianValue = getMedian(allValues);

      return (
        <div class="w-full overflow-x-auto">
          <div class="min-w-[800px]">
            <div
              style={`grid-template-columns: 100px repeat(${HOURS.length}, minmax(30px, 100px))`}
              class="border py-2 mb-4 grid gap-1 mb-1"
            >
              <div class="text-sm "></div>
              {HOURS.map((hour, index) => (
                <div key={hour} class="text-sm  text-center">
                  {index % 3 === 0 ? formatHour(hour) : ""}
                </div>
              ))}
            </div>

            {DAYS.map((day, dayIndex) => (
              <div
                key={day}
                style={`grid-template-columns: 100px repeat(${HOURS.length}, minmax(30px, 100px))`}
                class="grid gap-0 mb-0"
              >
                <div
                  class={
                    "text-sm  flex items-center border-l border-r flex justify-center items-center mr-2 " +
                    (dayIndex === 0 ? "border-t" : "") +
                    (dayIndex === DAYS.length - 1 ? "border-b" : "")
                  }
                >
                  {t(DAY_TRANSLATION_KEYS[day])}
                </div>
                {HOURS.map((hour) => {
                  const value = processedData[dayIndex][HOURS.indexOf(hour)];
                  const toolTip =
                    props.filters.metricInput === "revenue"
                      ? `${t(DAY_TRANSLATION_KEYS[day])} ${formatHour(
                          hour,
                        )} = ${formatPriceInLocale(
                          value,
                          true,
                          0,
                          auth.user.value?.restaurant.currency || "CHF",
                        )}`
                      : `${t(DAY_TRANSLATION_KEYS[day])} ${formatHour(
                          hour,
                        )} = ${value} ${t("reports.heatmap.orders")}`;
                  return (
                    <div class="p-1 flex flex-col">
                      <div
                        key={hour}
                        class={
                          "flex-1 aspect-square rounded tooltip tooltip-secondary tooltip-left border-2 l-box-shadow" +
                          (value > 0
                            ? "border-transparent hover:border-white"
                            : "border-[var(--component-border-color)]")
                        }
                        style={`background-color: ${getHeatmapColor(
                          value,
                          medianValue,
                          maxValue,
                        )}`}
                        data-tip={value > 0 ? toolTip : undefined}
                        title={value > 0 ? toolTip : undefined}
                      />
                    </div>
                  );
                })}
              </div>
            ))}

            <div class="mt-6 flex items-center justify-end gap-4">
              <div class="text-sm ">
                {t("reports.heatmap.min")} {formatNumber(minValue)}
              </div>
              {colors.map((color, idx) => (
                <div key={color} class="flex flex-col items-center gap-1">
                  <div
                    class="w-4 h-4 rounded"
                    style={`background-color: ${color}; border: 1px solid rgba(255,255,255,0.1);`}
                  />
                  {idx === Math.floor(colors.length / 2) && (
                    <div class="text-xs ">{formatNumber(medianValue)}</div>
                  )}
                </div>
              ))}
              <div class="text-sm ">
                {t("reports.heatmap.max")} {formatNumber(maxValue)}
              </div>
            </div>
          </div>
        </div>
      );
    };
  },
});
